import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Table,
  InputGroup,
  FormControl,
  Spinner,
  Badge,
  Form,
  ProgressBar,
} from "react-bootstrap";
import { Avatar } from "@mui/joy";
import { toast } from "react-toastify";
import {
  get_all_payment_plans,
  update_payment_plan_logistics,
} from "../reduxData/user/userAction";

const PlayLaterAdmin = () => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all"); 
  const [searchTerm, setSearchTerm] = useState("");

  const fetchPlans = async () => {
    setIsLoading(true);

    let apiParams = {};
    if (filterStatus === "ready_to_ship") {
      apiParams.shippingStatus = "pending";
    } else if (filterStatus === "shipped") {
      apiParams.shippingStatus = "shipped";
    } else if (filterStatus !== "all") {
      apiParams.status = filterStatus;
    }

    const data = await get_all_payment_plans(apiParams);
    setPlans(data || []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchPlans();
  }, [filterStatus]);

  const filteredPlans = useMemo(() => {
    if (!searchTerm) return plans;
    const lowerTerm = searchTerm.toLowerCase();

    return plans.filter((plan) => {
      const title = plan.itemSnapshot?.title?.toLowerCase() || "";
      const buyerName = plan.buyer?.user_name?.toLowerCase() || "";
      const buyerEmail = plan.buyer?.email?.toLowerCase() || "";
      return (
        title.includes(lowerTerm) ||
        buyerName.includes(lowerTerm) ||
        buyerEmail.includes(lowerTerm)
      );
    });
  }, [searchTerm, plans]);

  const handleMarkShipped = async (planId, currentStatus) => {
    const newStatus = !currentStatus;

    const success = await update_payment_plan_logistics(planId, {
      itemShipped: newStatus,
    });

    if (success) {
      toast.success(newStatus ? "Marked as Shipped!" : "Marked as Not Shipped");
      setPlans((prev) =>
        prev.map((p) =>
          p._id === planId
            ? { ...p, logistics: { ...p.logistics, itemShipped: newStatus } }
            : p
        )
      );
    } else {
      toast.error("Failed to update status.");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge bg="primary">Active</Badge>;
      case "completed":
        return <Badge bg="success">Paid in Full</Badge>;
      case "defaulted":
        return <Badge bg="danger">Defaulted</Badge>;
      case "cancelled":
        return <Badge bg="secondary">Cancelled</Badge>;
      default:
        return (
          <Badge bg="light" text="dark">
            {status}
          </Badge>
        );
    }
  };

  const getNextDueDate = (plan) => {
    if (plan.status === "completed") return <span className="text-success small fw-bold">Paid</span>;
    if (plan.status === "cancelled") return <span className="text-muted small">Cancelled</span>;

    const nextInstallment = plan.installments?.find(
      (inst) => inst.status === "pending" || inst.status === "failed"
    );

    if (!nextInstallment) return <span className="text-muted small">-</span>;

    const isOverdue = new Date(nextInstallment.dueDate) < new Date();
    const isFailed = nextInstallment.status === "failed";

    return (
      <div className={isFailed || isOverdue ? "text-danger" : "text-dark"}>
        <div className="fw-bold" style={{ fontSize: "0.85rem" }}>
          {new Date(nextInstallment.dueDate).toLocaleDateString()}
        </div>
        <div className="small text-muted">
          ${nextInstallment.amount.toFixed(2)}
        </div>
        {isFailed && <Badge bg="danger" className="mt-1" style={{fontSize: '0.6rem'}}>FAILED</Badge>}
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold mb-0">Play Later Management</h3>
          <p className="text-muted mb-0">
            Manage financing plans and shipping logistics.
          </p>
        </div>
        <Button variant="outline-primary" onClick={fetchPlans}>
          <i className="fas fa-sync me-2"></i>Refresh
        </Button>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">View All Plans</option>
                <option value="ready_to_ship">
                  Ready to Ship (Paid & Pending)
                </option>
                <option value="active">Active (Paying)</option>
                <option value="shipped">Shipped History</option>
                <option value="defaulted">Defaulted / Failed</option>
              </Form.Select>
            </div>
            <div className="col-md-8">
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <i className="fas fa-search text-muted"></i>
                </InputGroup.Text>
                <FormControl
                  placeholder="Search by Item Title, Username, or Email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="text-center p-5">
              <Spinner animation="border" variant="warning" />
            </div>
          ) : (
            <Table striped hover responsive className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "50px" }}>Item</th>
                  <th>Title</th>
                  <th>Buyer</th>
                  <th>Plan Progress</th>
                  <th>Balance</th>
                  <th>Next Due</th>
                  <th>Status</th>
                  <th>Logistics</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlans.length > 0 ? (
                  filteredPlans.map((plan) => {
                    const total = plan.financials.totalPlanCost;
                    const paid = total - plan.financials.balanceRemaining;
                    const progress = (paid / total) * 100;

                    return (
                      <tr key={plan._id}>
                        <td>
                          <Avatar
                            src={plan.itemSnapshot?.image}
                            variant="rounded"
                            sx={{ width: 40, height: 40 }}
                          />
                        </td>
                        <td className="fw-medium">
                          {plan.itemSnapshot?.title}
                          <div className="small text-muted">
                            ID: {plan._id.substring(plan._id.length - 6)}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Avatar
                              src={plan.buyer?.profile_pic}
                              size="sm"
                              className="me-2"
                              sx={{ width: 24, height: 24 }}
                            />
                            <div>
                              <div>{plan.buyer?.user_name || "Unknown"}</div>
                              <div
                                className="small text-muted"
                                style={{ fontSize: "0.75rem" }}
                              >
                                {plan.buyer?.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ minWidth: "150px" }}>
                          <ProgressBar
                            now={progress}
                            variant={progress >= 100 ? "success" : "warning"}
                            style={{ height: "6px" }}
                            className="mb-1"
                          />
                          <div className="d-flex justify-content-between small text-muted">
                            <span>{Math.round(progress)}%</span>
                            <span>{plan.installments?.length} mos</span>
                          </div>
                        </td>
                        <td className="fw-bold text-dark">
                          ${plan.financials.balanceRemaining.toFixed(2)}
                          <div className="small text-muted fw-normal">
                            Total: ${plan.financials.totalPlanCost.toFixed(2)}
                          </div>
                        </td>
                        <td>{getNextDueDate(plan)}</td>
                        <td>{getStatusBadge(plan.status)}</td>
                        <td>
                          {plan.status === "completed" ? (
                            <Form.Check
                              type="switch"
                              id={`ship-switch-${plan._id}`}
                              label={
                                plan.logistics.itemShipped
                                  ? "Shipped"
                                  : "Pending"
                              }
                              checked={plan.logistics.itemShipped}
                              onChange={() =>
                                handleMarkShipped(
                                  plan._id,
                                  plan.logistics.itemShipped
                                )
                              }
                              className={
                                plan.logistics.itemShipped
                                  ? "text-success"
                                  : "text-warning fw-bold"
                              }
                            />
                          ) : (
                            <span className="text-muted small">
                              <i className="fas fa-lock me-1"></i>
                              Await Pmt
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center p-5 text-muted">
                      No payment plans found matching your filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayLaterAdmin;